import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FileDeleteResponseDto, FileModel, ListSearch } from '@ca/core/models';
import { FileService } from '@ca/core/services';
import { BehaviorSubject, EMPTY, Observable, forkJoin, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Folder } from 'src/app/core/models/folder.model';

@Injectable()
export class MyFilesFacadeService {
    private currentFolder$$ = new BehaviorSubject<Folder | undefined>(undefined);
    private renameFolder$$ = new BehaviorSubject<Folder | undefined>(undefined);
    private totalItemsCount$$ = new BehaviorSubject<number>(0);
    private isFolderLoading$$ = new BehaviorSubject<boolean>(false);
    private newFolder$$ = new BehaviorSubject<Folder | undefined>(undefined);
    private deleteFoldersAndFiles$$ = new BehaviorSubject<{ folderIds: string[]; fileIds: string[] }>({ folderIds: [], fileIds: [] });
    private selectAllFilteredFilesOfFolder$$ = new BehaviorSubject<FileModel[] | undefined>(undefined);
    private error$$ = new BehaviorSubject<string | { statusOrMessage: string; folderName: string } | undefined>(undefined);

    get currentFolder$(): Observable<Folder | undefined> {
        return this.currentFolder$$.asObservable();
    }

    get renameFolder$(): Observable<Folder | undefined> {
        return this.renameFolder$$.asObservable();
    }

    get totalItemsCount$(): Observable<number> {
        return this.totalItemsCount$$.asObservable();
    }

    get newFolder$(): Observable<Folder | undefined> {
        return this.newFolder$$.asObservable();
    }

    get deleteFoldersAndFiles$(): Observable<{ folderIds: string[]; fileIds: string[] }> {
        return this.deleteFoldersAndFiles$$.asObservable();
    }

    get isFolderLoading$(): Observable<boolean> {
        return this.isFolderLoading$$.asObservable();
    }

    get selectAllFilteredFilesOfFolder$(): Observable<FileModel[] | undefined> {
        return this.selectAllFilteredFilesOfFolder$$.asObservable();
    }

    get error$(): Observable<string | { statusOrMessage: string; folderName: string } | undefined> {
        return this.error$$.asObservable();
    }

    constructor(private fileService: FileService) {}

    getFolder(folderId?: string, searchParams?: ListSearch): void {
        this.isFolderLoading$$.next(true);
        this.fileService
            .getFolder$(folderId, searchParams)
            .pipe(
                tap((folder: Folder) => {
                    this.currentFolder$$.next(folder);
                    this.totalItemsCount$$.next(folder.totalFilesCount + folder.totalFolderCount);
                }),
                catchError(() => {
                    this.error$$.next('MY_WORKSPACE.MY_FILES.ERROR_LOADING_FOLDER_CONTENT');
                    return EMPTY;
                }),
            )
            .subscribe()
            .add(() => {
                this.isFolderLoading$$.next(false);
            });
    }

    deleteBySearchParams(searchParams: ListSearch, folderId?: string): void {
        this.isFolderLoading$$.next(true);
        this.fileService
            .deleteBySearchParams$(searchParams.searchQuery, folderId)
            .pipe(
                tap((files: FileDeleteResponseDto[]) => {
                    searchParams.pageNumber = 1;
                    searchParams.searchQuery = '';
                    if (files.some(f => f.error != null)) {
                        this.error$$.next('MY_WORKSPACE.MY_FILES.ERROR_LOADING_FOLDER_CONTENT');
                    }

                    this.getFolder(folderId, searchParams);
                }),
                catchError(() => {
                    this.error$$.next('MY_WORKSPACE.MY_FILES.ERROR_LOADING_FOLDER_CONTENT');
                    return EMPTY;
                }),
            )
            .subscribe()
            .add(() => {
                this.isFolderLoading$$.next(false);
            });
    }

    renameFolder(folder: Folder, name: string): void {
        this.isFolderLoading$$.next(true);

        this.fileService
            .renameFolder$(folder, name)
            .pipe(
                tap(() => {
                    folder.name = name;
                    this.renameFolder$$.next(folder);
                }),
                catchError(() => {
                    this.error$$.next('MY_WORKSPACE.MY_FILES.ERROR_FOLDER_RENAME');
                    return EMPTY;
                }),
            )
            .subscribe()
            .add(() => {
                this.isFolderLoading$$.next(false);
            });
    }

    selectAllFilteredFilesOfFolder(folderId: string, searchParams?: ListSearch): void {
        this.isFolderLoading$$.next(true);
        const params: ListSearch = {
            pageNumber: 1,
            pageSize: 1000000,
            sortBy: searchParams?.sortBy || '-createdAt',
            searchQuery: searchParams?.searchQuery || '',
        };
        this.fileService
            .getFolder$(folderId, params)
            .pipe(
                tap((folder: Folder) => {
                    this.selectAllFilteredFilesOfFolder$$.next(folder.files as FileModel[] | undefined);
                }),
                catchError(() => {
                    this.error$$.next('MY_WORKSPACE.MY_FILES.ERROR_LOADING_FOLDER_CONTENT');
                    return EMPTY;
                }),
            )
            .subscribe()
            .add(() => {
                this.isFolderLoading$$.next(false);
            });
    }

    createFolder(parentFolderId: string, folderName: string): void {
        this.isFolderLoading$$.next(true);
        this.fileService
            .createFolder$(parentFolderId, folderName)
            .pipe(
                tap((newFolder: Folder) => {
                    this.newFolder$$.next(newFolder);
                }),
                catchError((err: HttpErrorResponse) => {
                    this.error$$.next({ statusOrMessage: `${err.status}`, folderName });
                    return EMPTY;
                }),
            )
            .subscribe()
            .add(() => {
                this.isFolderLoading$$.next(false);
            });
    }

    deleteFoldersAndFiles(itemsToDelete: Array<FileModel | Folder>): void {
        this.isFolderLoading$$.next(true);
        const fileIds = itemsToDelete.reduce((ids: string[], fileOrFolder: FileModel | Folder) => {
            if (fileOrFolder instanceof FileModel) {
                ids.push(fileOrFolder.id);
            }
            return ids;
        }, []);

        const folders = itemsToDelete.reduce((result: Folder[], fileOrFolder: FileModel | Folder) => {
            if (fileOrFolder instanceof Folder) {
                result.push(fileOrFolder);
            }
            return result;
        }, []);

        const deleteFolders$ =
            folders.length > 0
                ? forkJoin(
                      folders.map(folder =>
                          this.fileService.deleteFolder$(folder.id).pipe(
                              tap(resp => {
                                  this.fileService.checkFolderDeletionStatus(resp, folders);
                              }),
                              catchError(() => {
                                  this.error$$.next({ statusOrMessage: 'MY_WORKSPACE.MY_FILES.ERROR_DELETING_FOLDER', folderName: folder.name });
                                  return EMPTY;
                              }),
                          ),
                      ),
                  )
                : of(null);

        const deleteFiles$ =
            fileIds.length > 0
                ? this.fileService.deleteFiles$(fileIds).pipe(
                      tap((resp: FileDeleteResponseDto[]) => {
                          this.fileService.checkFilesDeletionStatus(fileIds, resp);
                      }),
                      catchError(() => {
                          this.error$$.next('MY_WORKSPACE.MY_FILES.ERROR_DELETING_FILES');
                          return EMPTY;
                      }),
                  )
                : of(null);

        forkJoin([deleteFolders$, deleteFiles$])
            .subscribe(() => {
                this.deleteFoldersAndFiles$$.next({ folderIds: folders.map(f => f.id), fileIds });
            })
            .add(() => {
                this.isFolderLoading$$.next(false);
            });
    }
}
